import { z } from "zod";
import { currentUserSchema } from "./session";

/**
 * The four first-login steps, in order: about you, first project, invite your team, all set.
 * `currentStep` is 1-based to match the board labels ("Step N of 4"). A reload resumes at the
 * saved step; once `completedAt` is set the flow never shows again.
 */
export const ONBOARDING_TOTAL_STEPS = 4;

export const onboardingSteps = ["about-you", "first-project", "invite-team", "all-set"] as const;
export const onboardingStepSchema = z.enum(onboardingSteps);
export type OnboardingStep = z.infer<typeof onboardingStepSchema>;

/** The "Your role" select on step 1, as the Onboarding1 board draws it. */
export const onboardingRoles = [
  "software-engineer",
  "engineering-manager",
  "qa",
  "product",
  "other",
] as const;
export const onboardingRoleSchema = z.enum(onboardingRoles);
export type OnboardingRole = z.infer<typeof onboardingRoleSchema>;

/** The "What will you use Zworks for?" checkboxes on step 1. At least one is chosen. */
export const onboardingUses = ["ship-features", "review-quality", "migrations"] as const;
export const onboardingUseSchema = z.enum(onboardingUses);
export type OnboardingUse = z.infer<typeof onboardingUseSchema>;

/** Step 1 payload. The name arrives prefilled from the provider; the terms box must be ticked. */
export const onboardingProfileSchema = z.object({
  displayName: z.string().trim().min(1),
  role: onboardingRoleSchema,
  uses: z.array(onboardingUseSchema).min(1),
  agreedToTerms: z.literal(true),
});
export type OnboardingProfile = z.infer<typeof onboardingProfileSchema>;

/**
 * The onboarding state, as `GET /api/v1/me` returns it. `currentStep` is null once the flow is
 * complete or skipped. `projectId` names the project step 2 created, so step 3 can invite into
 * it and step 4 can summarise it.
 */
export const onboardingStateSchema = z.object({
  currentStep: z.number().int().min(1).max(ONBOARDING_TOTAL_STEPS).nullable(),
  completedAt: z.iso.datetime().nullable(),
  profile: onboardingProfileSchema.nullable(),
  projectId: z.uuid().nullable(),
});
export type OnboardingState = z.infer<typeof onboardingStateSchema>;

/** `GET /api/v1/me`: the signed-in person plus the onboarding state. */
export const meResponseSchema = currentUserSchema.extend({
  onboarding: onboardingStateSchema,
});
export type MeResponse = z.infer<typeof meResponseSchema>;

/**
 * `POST /api/v1/me/onboarding`: saves the step payload and the current step. `completed` records
 * completion or skip and sets `completedAt`; skipping still leaves the step-2 project usable.
 */
export const saveOnboardingSchema = z.object({
  currentStep: z.number().int().min(1).max(ONBOARDING_TOTAL_STEPS),
  profile: onboardingProfileSchema.optional(),
  projectId: z.uuid().optional(),
  completed: z.boolean().optional(),
});
export type SaveOnboarding = z.infer<typeof saveOnboardingSchema>;
